// Sentry initialisation for the Vue 2 frontend.
//
// The DSN comes from `window.config.SENTRY_DSN` (loaded at runtime from
// `public/config.js`, which differs per environment). The init is a no-op
// when no DSN is configured, so the SDK can ship before the sentry.io
// project exists.
//
// We target @sentry/vue v7 because v8+ dropped Vue 2 support.

import * as Sentry from '@sentry/vue'

export function initSentry(Vue, router) {
  const dsn = (window.config && window.config.SENTRY_DSN) || ''
  if (!dsn) {
    // Silent no-op in dev / when SaaS isn't wired yet.
    return
  }

  const environment =
    (window.config && window.config.SENTRY_ENVIRONMENT) ||
    process.env.NODE_ENV ||
    'development'

  const release = process.env.VUE_APP_VERSION || undefined

  const integrations = []
  if (typeof Sentry.browserTracingIntegration === 'function') {
    integrations.push(
      Sentry.browserTracingIntegration({
        router,
        // Capture route transitions as transactions so we get Vue
        // navigation timings out of the box.
      })
    )
  }
  if (typeof Sentry.replayIntegration === 'function') {
    integrations.push(
      Sentry.replayIntegration({
        // Mask everything by default — CarTradePro forms carry vehicle
        // chassis numbers, customer IC numbers, and bank account numbers.
        // Replays only fire on errors anyway (see below).
        maskAllText: true,
        blockAllMedia: true,
      })
    )
  }

  Sentry.init({
    Vue,
    dsn,
    environment,
    release,
    integrations,
    tracesSampleRate:
      window.config && typeof window.config.SENTRY_TRACES_SAMPLE_RATE === 'number'
        ? window.config.SENTRY_TRACES_SAMPLE_RATE
        : 0.1,
    // No idle-session replays; only sample replays when an error occurs.
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate:
      window.config &&
      typeof window.config.SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE === 'number'
        ? window.config.SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE
        : 0.1,
    sendDefaultPii: false,
    // Bubble Vue lifecycle errors into Sentry AND keep them visible in the
    // console during development.
    logErrors: true,
  })

  // Make the SDK reachable from the axios interceptor + ad-hoc captures
  // without forcing every caller to import the module.
  window.Sentry = Sentry
}
